import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import weImage from '../assets/brand/we.jpg';
import cardImage from '../assets/brand/card.png';
import whyImage from '../assets/brand/why.jpg';
import p1 from '../assets/brand/p1.svg';
import p2 from '../assets/brand/p2.svg';
import p3 from '../assets/brand/p3.svg';

type ServiceTone = 'blue' | 'dark' | 'image' | 'image-dark';

type ServiceItem = {
  id: string;
  name: string;
  headline: string;
  description: string;
  includes: readonly string[];
  cta: string;
  ctaHref: string;
  tone: ServiceTone;
  image?: string;
  cardSummary: string;
};

const SERVICES: readonly ServiceItem[] = [
  {
    id: 'custom-software',
    name: 'Custom Software Development',
    headline: 'Software Designed Around Your Operations',
    description:
      'Off-the-shelf software forces you to change how you work. We take the opposite approach. We study your operations, understand your constraints, and build platforms that fit your actual workflows — not the other way around.',
    includes: [
      'Custom internal tools and operational dashboards',
      'Customer-facing portals and platforms',
      'Integration with existing systems and databases',
      'Scalable architecture that grows with your organisation',
    ],
    cta: 'Tell Us About Your Project',
    ctaHref: '/contact?subject=Tell%20Us%20About%20Your%20Project',
    tone: 'blue',
    cardSummary: 'Platforms built around your real workflows — not the other way around.',
  },
  {
    id: 'web-mobile',
    name: 'Web & Mobile Development',
    headline: 'Digital Experiences That Help You Serve Customers Better',
    description:
      'Your website or application is often the first impression people have of your organisation. We build responsive, accessible digital experiences that work reliably across devices — from desktop to the most common mobile phones in use today.',
    includes: [
      'Corporate and institutional websites',
      'Mobile-responsive web applications',
      'Cross-platform mobile apps',
      'Performance optimisation and accessibility compliance',
    ],
    cta: 'Tell Us About Your Project',
    ctaHref: '/contact?subject=Tell%20Us%20About%20Your%20Project',
    tone: 'image',
    image: cardImage,
    cardSummary: 'Responsive websites and apps that are practical, accessible, and built for real users.',
  },
  {
    id: 'process-automation',
    name: 'Business Process Automation',
    headline: 'Replace Manual Workflows With Automated Systems',
    description:
      'If your team is still using spreadsheets, paper forms, and email chains to manage critical operations, you’re losing time and introducing errors. We design and build automated systems that connect your processes, reduce manual work, and free your team to focus on higher-value tasks.',
    includes: [
      'Workflow digitisation and automation',
      'Document and records management systems',
      'Approval and escalation workflows',
      'Integration between disconnected tools',
    ],
    cta: 'Tell Us About Your Project',
    ctaHref: '/contact?subject=Tell%20Us%20About%20Your%20Project',
    tone: 'dark',
    cardSummary: 'Connect disconnected tools and automate repetitive work so teams focus on what matters.',
  },
  {
    id: 'ui-ux',
    name: 'UI/UX Design',
    headline: 'Interfaces That Make Complex Tools Feel Simple',
    description:
      'The best software is useless if people can’t figure out how to use it. We design interfaces that are intuitive, accessible, and built around real user behaviour — not just what looks good in a mockup.',
    includes: [
      'User research and usability testing',
      'Information architecture and user flows',
      'Wireframing and interactive prototypes',
      'Visual design and design systems',
    ],
    cta: 'Tell Us About Your Project',
    ctaHref: '/contact?subject=Tell%20Us%20About%20Your%20Project',
    tone: 'image-dark',
    image: whyImage,
    cardSummary: 'Intuitive interfaces designed around real behaviour — not just polished mockups.',
  },
  {
    id: 'quality-assurance',
    name: 'Quality Assurance & Testing',
    headline: 'Release Software With Greater Confidence',
    description:
      'Bugs caught during development cost a fraction of what they cost in production. We test your software thoroughly — functional, performance, security, and usability — before it reaches your users.',
    includes: [
      'Manual and automated testing',
      'Functional and regression testing',
      'Performance and load testing',
      'Test planning and quality strategy',
    ],
    cta: 'Tell Us About Your Project',
    ctaHref: '/contact?subject=Tell%20Us%20About%20Your%20Project',
    tone: 'blue',
    cardSummary: 'Thorough testing before release so your users get reliable software.',
  },
  {
    id: 'consultancy',
    name: 'Technology Consultancy',
    headline: 'Strategic Guidance to Modernise Your Tech Stack',
    description:
      'Not sure what technology you need, or whether your current systems are still serving you? We help organizations assess their technology landscape, identify gaps and opportunities, and build a practical roadmap for the future.',
    includes: [
      'Technology audits and assessments',
      'Digital transformation strategy',
      'Tool selection and vendor evaluation',
      'Architecture and roadmap planning',
    ],
    cta: 'Book a Consultation',
    ctaHref: '/contact?subject=Book%20a%20Consultation',
    tone: 'dark',
    cardSummary: 'Clear assessments and practical roadmaps to modernise your stack with confidence.',
  },
] as const;

const panelClass = (tone: ServiceTone) => {
  if (tone === 'blue') return 'bg-gradient-to-b from-[#2776EA] to-[#164384]';
  if (tone === 'dark') return 'bg-[#212121]';
  return 'relative overflow-hidden';
};

const Services: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [location.hash]);

  return (
    <div className="w-full bg-white">
      <section className="bg-white pb-10 pt-16">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Our Services
            </Badge>
            <h1 className="mt-3 font-title text-[42px] font-semibold leading-none text-[#212121] max-[700px]:text-[32px]">
              What We Build
            </h1>
            <p className="mx-auto mt-2 max-w-[560px] font-description text-[12px] font-light leading-[1.4] text-[#919191]">
              We help organizations modernise their operations through practical, well-built technology. Here’s how.
            </p>
          </div>

          <div className="mx-auto mt-8 grid max-w-[1280px] grid-cols-3 gap-2 max-[980px]:grid-cols-2 max-[700px]:grid-cols-1">
            {SERVICES.map((service, index) => {
              const useImageCard = index % 3 === 1 && Boolean(service.image);
              const useBlueCard = index % 3 === 0;

              if (useImageCard && service.image) {
                return (
                  <Link key={service.id} to={`/services#${service.id}`} className="block h-full">
                    <Card
                      className="relative h-full min-h-[250px] overflow-hidden rounded-[20px] px-3.5 pb-4 pt-5 max-[980px]:min-h-[210px]"
                      style={{
                        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.88) 100%), url(${service.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    >
                      <div className="flex h-full min-h-[220px] items-end">
                        <div>
                          <h2 className="font-title text-[22px] font-medium leading-[1.08] text-white md:text-[24px]">
                            {service.name}
                          </h2>
                          <p className="mt-2 max-w-[270px] font-description text-[11px] font-light leading-[1.45] text-white">
                            {service.cardSummary}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              }

              if (useBlueCard) {
                return (
                  <Link key={service.id} to={`/services#${service.id}`} className="block h-full">
                    <Card className="flex h-full min-h-[250px] flex-col rounded-[20px] bg-gradient-to-b from-[#2776EA] to-[#164384] px-3.5 pb-4 pt-5 max-[980px]:min-h-[210px]">
                      {index === 0 ? (
                        <div className="flex items-center">
                          <img src={p1} alt="" aria-hidden="true" className="h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white" />
                          <img src={p2} alt="" aria-hidden="true" className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white" />
                          <img src={p3} alt="" aria-hidden="true" className="-ml-2 h-9 w-9 rounded-full border-2 border-[#2776EA] bg-white" />
                        </div>
                      ) : (
                        <Badge className="inline-flex w-fit rounded-full border border-white/25 bg-white/10 px-2.5 py-[3px] font-description text-[9px] font-normal text-white/80">
                          Service
                        </Badge>
                      )}
                      <div className="mt-auto pt-8">
                        <h2 className="font-title text-[22px] font-medium leading-[1.08] text-white md:text-[24px]">
                          {service.name}
                        </h2>
                        <p className="mt-2 max-w-[270px] font-description text-[11px] font-light leading-[1.45] text-[#E4E4E4]">
                          {service.cardSummary}
                        </p>
                      </div>
                    </Card>
                  </Link>
                );
              }

              return (
                <Link key={service.id} to={`/services#${service.id}`} className="block h-full">
                  <Card className="flex h-full min-h-[250px] flex-col justify-end rounded-[20px] bg-[#212121] px-3.5 pb-4 pt-5 max-[980px]:min-h-[210px]">
                    <h2 className="font-title text-[22px] font-medium leading-[1.08] text-white md:text-[24px]">
                      {service.name}
                    </h2>
                    <p className="mt-2.5 max-w-[270px] font-description text-[11px] font-light leading-[1.45] text-white/90">
                      {service.cardSummary}
                    </p>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {SERVICES.map((service, index) => {
        const isImageTone = service.tone === 'image' || service.tone === 'image-dark';

        return (
          <section key={service.id} id={service.id} className="scroll-mt-24 bg-white pb-16">
            <div className="mx-auto max-w-[1360px] px-5">
              {isImageTone && service.image ? (
                <div
                  className="relative min-h-[320px] overflow-hidden rounded-[24px] p-8 md:min-h-[360px] md:p-12 flex items-end"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15) 20%, rgba(0,0,0,0.88) 100%), url(${service.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="max-w-[720px]">
                    <p className="font-title text-[11px] font-medium uppercase tracking-wider text-white/70">
                      {service.name}
                    </p>
                    <h2 className="mt-2 font-title text-[28px] font-medium leading-[1.1] text-white md:text-[36px]">
                      {service.headline}
                    </h2>
                    <p className="mt-4 font-description text-[13px] font-light leading-[1.55] text-white/90 md:text-[14px]">
                      {service.description}
                    </p>
                    <div className="mt-6">
                      <h3 className="font-title text-[13px] font-medium text-white">What this includes</h3>
                      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                        {service.includes.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                            <span className="font-description text-[12px] font-light leading-[1.45] text-white/85">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link
                      to={service.ctaHref}
                      className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#434343] px-5 py-2 font-title text-[12px] font-medium text-white hover:bg-[#555] transition-colors"
                    >
                      {service.cta}
                      <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>
              ) : index === SERVICES.length - 1 ? (
                <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
                  <div className={`rounded-[24px] p-8 md:p-12 flex flex-col justify-center ${panelClass(service.tone)}`}>
                    <p className="font-title text-[11px] font-medium uppercase tracking-wider text-white/70">
                      {service.name}
                    </p>
                    <h2 className="mt-2 font-title text-[28px] font-medium leading-[1.1] text-white md:text-[36px]">
                      {service.headline}
                    </h2>
                    <p className="mt-4 font-description text-[13px] font-light leading-[1.55] text-white/90 md:text-[14px]">
                      {service.description}
                    </p>
                    <div className="mt-6">
                      <h3 className="font-title text-[13px] font-medium text-white">What this includes</h3>
                      <ul className="mt-3 space-y-2">
                        {service.includes.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                            <span className="font-description text-[12px] font-light leading-[1.45] text-white/85">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Link
                      to={service.ctaHref}
                      className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-[#434343] px-5 py-2 font-title text-[12px] font-medium text-white hover:bg-[#555] transition-colors"
                    >
                      {service.cta}
                      <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                  <div className="min-h-[260px] overflow-hidden rounded-[24px]">
                    <img src={weImage} alt="" className="h-full w-full object-cover" />
                  </div>
                </div>
              ) : (
                <div className={`rounded-[24px] p-8 md:p-12 ${panelClass(service.tone)}`}>
                  <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
                    <div>
                      <p className="font-title text-[11px] font-medium uppercase tracking-wider text-white/70">
                        {service.name}
                      </p>
                      <h2 className="mt-2 font-title text-[28px] font-medium leading-[1.1] text-white md:text-[36px]">
                        {service.headline}
                      </h2>
                      <p className="mt-4 max-w-[640px] font-description text-[13px] font-light leading-[1.55] text-[#E4E4E4] md:text-[14px]">
                        {service.description}
                      </p>
                      <Link
                        to={service.ctaHref}
                        className={`mt-7 inline-flex items-center gap-2 rounded-full px-5 py-2 font-title text-[12px] font-medium text-white transition-colors ${
                          service.tone === 'blue'
                            ? 'bg-white/20 hover:bg-white/30'
                            : 'bg-[#434343] hover:bg-[#555]'
                        }`}
                      >
                        {service.cta}
                        <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                    <div>
                      <h3 className="font-title text-[13px] font-medium text-white">What this includes</h3>
                      <ul className="mt-3 space-y-2.5 rounded-[16px] bg-white/10 p-5 backdrop-blur-[2px]">
                        {service.includes.map((item) => (
                          <li key={item} className="flex items-start gap-2.5">
                            <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                            <span className="font-description text-[12px] font-light leading-[1.45] text-white/90 md:text-[13px]">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })}

      <section className="bg-[#F8FAFE] py-16">
        <div className="mx-auto max-w-[800px] px-5 text-center">
          <h2 className="font-title text-[28px] font-medium text-[#212121] md:text-[34px]">
            Ready to discuss a project?
          </h2>
          <p className="mt-3 font-description text-[13px] font-light text-[#919191] md:text-[14px]">
            Tell us what you need and we will help you choose the right path forward.
          </p>
          <Link to="/contact?subject=Tell%20Us%20About%20Your%20Project" className="mt-6 inline-block">
            <Button className="rounded-[8px] bg-[#2776EA] px-6 py-2.5 font-description text-[13px] font-medium text-white hover:bg-blue-600 transition-colors">
              Tell Us About Your Project
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
